<template>
    <app-layout>
        <template #header>
            <AppHead :title="`Order #${order.id}`" />
            <h2 class="title">Order Details</h2>
            <flash-messages />
        </template>

        <div class="row mt-4">
            <div class="col-lg-6">
                <card title="Create Account" class="mb-3"
                    v-if="$page.props.auth.user.role == 'shop' && $page.props.auth.user.status_code != 'active'">
                    <form @submit.prevent="submitAccountSetup">
                        <div class="p-3">
                            <h3>Welcome to Mobile Tool Network! Create an account to come back and shop!</h3>

                            <jet-validation-errors class="mb-4" />

                            <div class="form-group" v-if="user.email && user.email.length > 0">
                                <jet-label for="email" value="Email" />
                                <jet-input id="email" type="email" name="email" class="form-control" v-model="form.email"
                                    autocomplete="username" disabled />
                            </div>

                            <div
                                v-if="(user.first_name && user.first_name.length > 0) && (user.email && user.email.length > 0)">
                            </div>
                            <div v-else>
                                <div class="form-group">
                                    <jet-label for="first_name" value="First Name" />
                                    <jet-input id="first_name" type="text" class="form-control" v-model="form.first_name"
                                        required />
                                </div>
                                <div class="form-group">
                                    <jet-label for="last_name" value="Last Name" />
                                    <jet-input id="last_name" type="text" class="form-control" v-model="form.last_name"
                                        required />
                                </div>
                                <div class="form-group" v-if="!user.email || user.email.length == 0">
                                    <jet-label for="email" value="Email" />
                                    <jet-input id="email" type="email" class="form-control" v-model="form.email"
                                        autocomplete="username" required />
                                </div>
                                <div class="form-group" v-if="!user.phone_number || user.phone_number.length == 0">
                                    <jet-label for="phone_number" value="Phone" />
                                    <jet-input id="phone_number" type="text" class="form-control"
                                        v-model="form.phone_number" required />
                                </div>
                            </div>

                            <div class="form-group">
                                <jet-label for="password" value="Password" />
                                <jet-input id="password" type="password" class="form-control" v-model="form.password"
                                    required autocomplete="new-password" />
                            </div>

                            <div class="form-group">
                                <jet-label for="password_confirmation" value="Confirm Password" />
                                <jet-input id="password_confirmation" type="password" class="form-control"
                                    v-model="form.password_confirmation" required autocomplete="new-password" />
                            </div>

                            <div class="mt-4" v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature">
                                <jet-label for="terms">
                                    <div class="flex items-center">
                                        <jet-checkbox name="terms" id="terms" v-model:checked="form.terms" />

                                        <div class="ml-2">
                                            I agree to the <a target="_blank" :href="route('terms.show')"
                                                class="underline text-sm text-gray-600 hover:text-gray-900">Terms of
                                                Service</a> and <a target="_blank" :href="route('policy.show')"
                                                class="underline text-sm text-gray-600 hover:text-gray-900">Privacy
                                                Policy</a>
                                        </div>
                                    </div>
                                </jet-label>
                            </div>

                            <div class="flex items-center justify-end mt-4">
                                <jet-button type="submit" :disabled="form.processing">Complete Setup</jet-button>
                            </div>
                        </div>
                    </form>
                </card>

                <OrderInfo :order="order" />

                <card title="Shipping To" class="mt-3">
                    <p v-if="order.shipping_address" class="mb-0">
                        {{ order.shipping_address.address1 }} {{ order.shipping_address.address2 }}<br />
                        {{ order.shipping_address.city }}, {{ order.shipping_address.state }} {{
                            order.shipping_address.postcode }}
                    </p>
                    <p v-else class="mb-0">
                        {{ $page.props.auth.user.role == 'shop' ? $page.props.auth.user.mtd.company_name :
                            $page.props.auth.user.company_name }}
                    </p>
                </card>

                <card v-if="order.payment_method" title="Payment Method" class="mt-3">
                    <div v-if="order.payment_method.payment_type == 'Credit Card'">
                        <img :src="order.payment_method.cc_image_url" :alt="order.payment_method.brand" width="32" /> ending
                        in {{ order.payment_method.last4
                        }}
                    </div>
                    <p v-else class="mb-0">{{ order.payment_method.bank_name }} ending in {{ order.payment_method.last4 }}
                    </p>
                </card>

                <card v-if="shipments.length > 0"
                    :title="`${shipments.length} ${shipments.length > 1 ? 'Shipments' : 'Shipment'}`" class="mt-3">
                    <div class="order-list-item" v-for="shipItem in shipments" :key="shipItem.id">
                        <div>
                            <h4>Order-Shipment #{{ shipItem.id }}</h4>
                        </div>
                        <div>
                            <h4>Shipment Total: {{ formatDollars(shipItem.total) }}</h4>
                        </div>
                        <div v-if="shipItem.status_code == 'backorder'">
                            <h4>Backorder - Expected to Ship
                                {{ moment.utc(shipItem.backorder_eta).format('dddd MMM D, YYYY') }}</h4>
                        </div>
                        <div v-else-if="shipItem.status_code == 'shipped'">
                            <div v-if="shipItem.events.length > 0">
                                <h4 v-if="shipItem.delivery_date">Arriving {{
                                    moment.utc(shipItem.delivery_date).format('dddd MMM D, YYYY') }}</h4>
                                <strong>Last Update:</strong>
                                {{ moment(shipItem.events[0].event_date).format('M/D/YY h:mm A') }}
                                {{ shipItem.events[0].event_description }} {{ shipItem.events[0].event_city }}, {{
                                    shipItem.events[0].event_state }}
                                <div>
                                    <button class="btn btn-outline-primary" @click="selectTrack(shipItem)">Track
                                        Package</button>
                                </div>
                            </div>
                            <div v-else>
                                <h4>Shipped {{ moment(shipItem.shipped_at).format('dddd MMM D, YYYY') }}</h4>
                            </div>
                        </div>
                        <div v-else-if="shipItem.status_code == 'delivered'">
                            <h4>Delivered {{ moment(shipItem.delivered_at).format('MMM D, YYYY') }}</h4>
                        </div>
                        <div v-else>
                            <h4>Prepared {{ moment(shipItem.created_at).format('MMM D, YYYY') }}</h4>
                        </div>

                        <div class="order-list-item mt-3">
                            <template v-for="item in shipItem.items" :key="item.id">
                                <ProductRow :item="item" :sku="item.product_sku" productRoute="products.view"
                                    productStyle="inline">
                                    <p class="qty text-size-small lh-small m-0">{{ item.qty }} {{ item.qty > 1 ? 'items' :
                                        'item' }} in this shipment</p>
                                    <p v-if="item.bundle_product_name">From bundle {{ item.bundle_product_name }}</p>
                                </ProductRow>
                            </template>
                        </div>
                    </div>
                </card>
            </div>
            <div class="col-sm-6">
                <card bodyClasses="py-3 px-0">
                    <OrderTable productRoute="products.view" :items="items" :backorderItems="[]" :order="order">
                        <div v-if="order.available_credit > 0" class="d-flex justify-content-between">
                            <strong>Account Credit
                                Applied</strong><span class="text-secondary">
                                -{{ formatDollars(order.available_credit) }}
                            </span>
                        </div>
                    </OrderTable>

                    <div class="px-3 mt-2">
                        <jet-button class="mt-2 float-right"
                            :goesTo="route($page.props.auth.user.role == 'mtd' ? 'mtd.return.create' : 'return.create', order)"
                            icon="fas fa-share">Return Items</jet-button>
                    </div>
                </card>
            </div>
        </div>

        <section v-if="payments.length > 0" class="mt-4">
            <h3 class="mb-2">Payments</h3>
            <DataTable :value="payments" class="p-datatable-sm" tableClass="border-left border-right border-bottom"
                breakpoint="992px" responsiveLayout="stack" :rowClass="() => { return 'table-row' }" stripedRows>
                <Column field="transaction_date" header="Date">
                    <template #body="slotProps">
                        {{ moment(slotProps.data.transaction_date).format('M/D/YY') }}
                    </template>
                </Column>
                <Column field="type" header="Payment">
                    <template #body="slotProps">
                        <PaymentType :payment="slotProps.data" />
                    </template>
                </Column>
                <Column field="amount" header="Amount">
                    <template #body="slotProps">
                        {{ formatDollars(- slotProps.data.applied_amount) }}
                    </template>
                </Column>
                <Column field="id" class="button-container pl-0" header="">
                    <template #body="slotProps">
                        <jet-button @click="openPrintPaymentReceipt(slotProps.data.id)" class="btn-sm"
                            icon="fas fa-angle-right" tabindex="-1">View</jet-button>
                    </template>
                </Column>
            </DataTable>
        </section>

        <div class="row mt-3" v-if="order.backorder_linked_order_id">
            <div class="col-12">
                <BackOrder productRoute="products.view" :orderRoute="route('order.view', order.backorder_linked_order_id)"
                    :order="backorder" />
            </div>
        </div>

        <Notes @addNote="addNote" :notes="notes" :formNote="formNote" textPlaceholder="Write a note for this order" />

        <jet-dialog-modal id="trackModal">
            <template #title>
                Package Tracking
            </template>
            <template #content>
                <div v-if="shipItem">
                    <table class="w-100">
                        <tr v-for="track in shipItem.events" :key="track.event_date">
                            <td>{{ moment(track.event_date).format('M/D/YY h:mm A') }} </td>
                            <td>{{ track.event_description }} </td>
                            <td v-if="track.event_city && track.event_city.length > 0">{{ track.event_city }}, {{
                                track.event_state }}</td>
                        </tr>
                    </table>
                </div>
            </template>

            <template #footer>
                <jet-button @click="hideTrack">Close</jet-button>
            </template>
        </jet-dialog-modal>
    </app-layout>
    <teleport to="body">
        <PrintModal :printUrl="printUrl" @print="printPaymentReceipt" />
    </teleport>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout'
import FlashMessages from '@/Shared/FlashMessages'
import JetDialogModal from '@/Jetstream/DialogModal'
import JetInputError from '@/Jetstream/InputError'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetLabel from '@/Jetstream/Label'
import JetValidationErrors from '@/Jetstream/ValidationErrors'
import OrderInfo from '@/Components/Shop/OrderInfo'
import OrderTable from '@/Components/Shop/OrderTable'
import Notes from '@/Shared/Notes'
import BackOrder from '@/Shared/BackOrder'
import ProductRow from '@/Components/Shop/ProductRow'
import moment from 'moment'
import PaymentType from '@/Components/Transaction/PaymentType.vue'
import PrintModal from '@/Components/Modals/PrintModal'

export default {
    components: {
        AppLayout,
        FlashMessages,
        JetInput,
        JetCheckbox,
        JetInputError,
        JetLabel,
        JetDialogModal,
        OrderTable,
        OrderInfo,
        Notes,
        ProductRow,
        JetValidationErrors,
        BackOrder,
        PaymentType,
        PrintModal,
    },
    props: {
        order: Object,
        backorder: Object,
        items: Array,
        payments: Array,
        shipments: Array,
        notes: Array,
        cash: Object,
        account_balance: Number,
        track_purchase: Boolean
    },
    data() {
        let user = this.order.user;

        return {
            user: user,
            shipItem: null,
            form: this.$inertia.form({
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                phone_number: user.phone_number,
                password: '',
                password_confirmation: '',
                terms: false,
            }),
            formNote: this.$inertia.form({
                _method: 'put',
                content: null
            }),
            selectedReceiptPaymentId: null,
            printUrl: '',
        }
    },
    created() {
        this.moment = moment;
    },
    mounted() {
        if (this.track_purchase) {
            this.trackPurchase(this.order, this.items);
        }
    },
    methods: {
        selectTrack(shipItem) {
            this.shipItem = shipItem;
            $('#trackModal').modal('show');
        },
        hideTrack() {
            this.shipItem = null;
            $('#trackModal').modal('hide');
        },
        addNote() {
            this.formNote.post(this.route('orders.addnote', this.order.id), {
                preserveScroll: true
            })
        },
        submitAccountSetup() {
            this.form.post(this.route('tu.setup.checkout'), {
                onFinish: () => this.form.reset('password', 'password_confirmation'),
            })
        },
        openPrintPaymentReceipt(paymentId) {
            this.selectedReceiptPaymentId = paymentId
            this.printUrl = this.route('tu.payment.receipt', this.selectedReceiptPaymentId);
            $('#PrintModal').modal('show');
        },
        printPaymentReceipt() {
            this.printUrl = this.route('tu.payment.receipt', { payment: this.selectedReceiptPaymentId, print: 1 });
        },
    },
}
</script>
